import type { FC } from 'react';
import type { AvailableCountries } from 'types/users.types';
import StepProgressList, {
  type StepProgressItem,
  type StepProgressStatus,
} from './StepProgressList';
// todo: fix cycle import
import {
  MAIN_STEPS,
  type StepMode,
  getMainStepForSubstep,
  getScopedMainSteps,
} from '../utils/stepDefinitions';
import checkboxReadyAnimation from './checkbox-ready-animation.json';

interface ActivationProgressSummaryProps {
  /** Current substep ID in the flow */
  currentSubstepId: string;

  /** Mode: 'before_current' shows all steps up to current, 'all' shows all steps */
  mode?: 'before_current' | 'all';

  /** Main step ID to animate checkmark with Lottie animation */
  animateStepId?: string;

  /** Filter main steps by the mode of their substeps (e.g. only pre-account steps) */
  stepsScope?: StepMode;

  /** User's country — used to exclude country-specific steps that don't apply */
  userCountry?: AvailableCountries;
}

/**
 * Dynamic component that displays progress summary showing main steps
 * Automatically determines completion status based on:
 * - Pre-account: current position in flow
 * - Post-account: backend activation status
 *
 * Used in:
 * - init_activation step (shows all main steps completed before post-account flow)
 * - ready_to_go step (shows all completed steps with animated checkmark)
 *
 * @example
 * // Animate checkmark for "embed_banking" step
 * <StepsProgressSummary
 *   currentSubstepId="ready_to_go"
 *   mode="all"
 *   animateStepId="embed_banking"
 * />
 */
const StepsProgressSummary: FC<ActivationProgressSummaryProps> = ({
  currentSubstepId,
  mode = 'before_current',
  animateStepId,
  stepsScope,
  userCountry,
}) => {
  const scopedSteps = stepsScope
    ? getScopedMainSteps(stepsScope, userCountry)
    : MAIN_STEPS;

  // Find current main step
  const currentMainStep = getMainStepForSubstep(currentSubstepId);
  const currentMainStepIndex = currentMainStep
    ? scopedSteps.findIndex((s) => s.id === currentMainStep.id)
    : -1;

  // Determine which main steps to show
  const mainStepsToShow =
    mode === 'before_current' && currentMainStepIndex >= 0
      ? scopedSteps.slice(0, currentMainStepIndex)
      : scopedSteps;

  // Build progress items for main steps
  const progressItems: StepProgressItem[] = mainStepsToShow.map((mainStep) => {
    const mainStepIndex = scopedSteps.findIndex((s) => s.id === mainStep.id);

    // Determine status based on whether all substeps are completed
    let status: StepProgressStatus;

    if (currentMainStepIndex === -1) {
      status = 'pending';
    } else if (mainStepIndex < currentMainStepIndex) {
      // Main steps before current are completed
      status = 'completed';
    } else if (mainStepIndex === currentMainStepIndex) {
      // Current main step is in progress
      status = 'current';
    } else {
      status = 'pending';
    }

    // Enable animation for specific step if:
    // 1. It matches the requested animateStepId
    // 2. It's completed OR it's current and we're on the last substep of this main step
    const isTargetStep = animateStepId === mainStep.id;
    const isLastSubstepOfMainStep =
      currentMainStep?.id === mainStep.id &&
      mainStep.substeps[mainStep.substeps.length - 1]?.id === currentSubstepId;
    const shouldAnimate =
      isTargetStep && (status === 'completed' || isLastSubstepOfMainStep);

    return {
      label: mainStep.label,
      status,
      icon: mainStep.listIcon,
      animateCheckmark: shouldAnimate,
      lottieAnimationData: shouldAnimate ? checkboxReadyAnimation : undefined,
    };
  });

  return <StepProgressList items={progressItems} showSubsteps={false} />;
};

export default StepsProgressSummary;
